import { StudioFrameInputSchema, StudioParametersSchema, StudioWebPageSchema, webNodeOperation, type StudioFrameInput, type StudioWorkflowImage } from '@genieartinc/schemas/graph'
const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
/** Resolution variants belong to one result. Only output-time metadata is provenance. */
export function workflowImages(state: Record<string, unknown>): StudioWorkflowImage[] {
  const outputs: StudioWorkflowImage[] = []
  for (const raw of Array.isArray(state.createdItems) ? state.createdItems : []) {
    const item = record(raw)
    if (item.type !== 'image' || typeof item.content !== 'string' || typeof item.id !== 'string') continue
    const meta = record(item.metadata), snapshot = record(meta.generationSnapshot), generated = record(meta.generatedParams)
    const original = record(record(meta.versions).original).url
    const settings = StudioParametersSchema.safeParse(snapshot.settings ?? meta.settings ?? generated.parameters)
    const inputs = StudioFrameInputSchema.array().safeParse(meta.studioInputs)
    const model = snapshot.modelId ?? generated.model
    outputs.push({ id: item.id, url: typeof original === 'string' && original ? original : item.content,
      label: typeof meta.name === 'string' ? meta.name : `Output ${outputs.length + 1}`,
      ...(typeof meta.jobId === 'string' ? { jobId: meta.jobId } : {}),
      ...(inputs.success ? { inputVersions: inputs.data } : {}),
      ...(settings.success ? { settings: settings.data } : {}), ...(typeof model === 'string' ? { model } : {}),
    })
  }
  if (typeof state.maskData === 'string' && state.maskData) outputs.push({ id: 'mask', url: state.maskData, label: 'Mask' })
  const file = record(state.fileMetadata)
  if (!outputs.length && typeof file.publicUrl === 'string') outputs.push({ id: String(state.fileUuid ?? 'file'), url: file.publicUrl, label: 'Image' })
  return outputs
}
/** Explicit pins reachable upstream, with bounded traversal even in a draft cycle. */
export function workflowInputs(nodes: Array<{ id: string; data: Record<string, unknown> }>, edges: Array<{ source: string; target: string }>, target: string): StudioFrameInput[] {
  const byId = new Map(nodes.map(node => [node.id, node])), incoming = new Map<string, string[]>()
  for (const edge of edges) incoming.set(edge.target, [...(incoming.get(edge.target) ?? []), edge.source])
  const seen = new Set<string>(), pins = new Set<string>(), inputs: StudioFrameInput[] = [], queue = [target]
  for (let index = 0; index < queue.length; index++) {
    const id = queue[index]; if (seen.has(id)) continue; seen.add(id)
    const input = StudioFrameInputSchema.safeParse(record(byId.get(id)?.data.state).frameInput)
    if (input.success) {
      const key = JSON.stringify(input.data)
      if (!pins.has(key)) { pins.add(key); inputs.push(input.data) }
    }
    queue.push(...(incoming.get(id) ?? []))
  }
  return inputs
}

/** One node of a web page, and the operation a runtime would execute for it. */
export interface StudioWebNodePlan {
  nodeId: string
  /** The page's own node type, e.g. `ImageNode`. */
  nodeType: string
  /** A key of GRAPH_NODE_DEFS, or null when this node runs nothing. */
  operation: string | null
  /** The built-in app that is the same capability, as a catalogue slug. */
  app?: string
}

/**
 * Read a web node page as operations.
 *
 * `webPageToStudioGraph` preserves a page verbatim as `web:<Type>` nodes, which is right for
 * storage and useless for planning a run. This is the other reading: for each node, the
 * operation from the catalogue it stands for — so a host can say what a page would do, and an
 * executor has something to schedule, without either of them knowing about React components.
 * Nodes that run nothing (comments, shaders, a mask editor) come back with a null operation
 * rather than being dropped, because their edges still shape the graph.
 */
export function webPagePlan(value: unknown): StudioWebNodePlan[] {
  const { nodes } = StudioWebPageSchema.parse(value)
  return nodes.map(node => {
    const state = record(record(node.data).state)
    const purpose = typeof state.purpose === 'string' ? state.purpose : undefined
    const subPurpose = typeof state.subPurpose === 'string' ? state.subPurpose : undefined
    const match = webNodeOperation(node.type, purpose, subPurpose)
    return { nodeId: node.id, nodeType: node.type, operation: match?.operation ?? null, ...(match?.app ? { app: match.app } : {}) }
  })
}

/**
 * Why this page cannot be run as a graph yet. Empty means every node maps to an operation the
 * catalogue defines and the wiring is sound; a host still decides whether it can afford or
 * reach those operations.
 */
export function webPageRunIssues(value: unknown): string[] {
  const plan = webPagePlan(value)
  const issues = plan.filter(node => !node.operation).map(node => `${node.nodeType} (${node.nodeId}) runs nothing on its own.`)
  if (!plan.some(node => node.operation === 'canvas.out')) issues.push('Add a Frame Output so the result lands on a frame.')
  return issues
}
